/**
 * Tab "Pay Data" của /purchase/control — Access `frmProcurementMainSubFormPayData`.
 * List (frmProcurementQryPayData → public.v_paydata_p) + toolbar 11 nút (theo Left):
 *   New · Edit · Copy · Add Invoice · Change Bank · Delete · Print-XNTT
 *   · Send-DNTT · Send-DNTT VAT · Send-XNTT.
 * Template dataList chuẩn (/convert-table): FilterBar + Search + per-column ▽ +
 * Columns menu (persist `procurement`/columns/procurement-pay-data) + freeze contract_no
 * + row-select toggle (Edit/Copy/Print/single-Delete) + tick-checkbox cột đầu cho
 * thao tác multi-row (Add Invoice / Change Bank / Delete nhiều / Send VAT).
 * Send-DNTT = tải file .eml (Excel DNTT + body + người nhận, X-Unsent:1) → user
 *   double-click mở Outlook ở chế độ soạn để review & tự Send (không auto-send;
 *   attachment auto-vào draft — mailto không đính kèm được). Send-XNTT = mailto
 *   (không kèm file) mở Outlook điền sẵn To/Subject/Body cho user review & tự Send.
 *   TẠM ép người nhận về email test (_MAIL_SEND_OVERRIDE_TO ở BE).
 */
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso, type TableVirtuosoHandle } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import {
  useListPayDataQuery,
  useDeletePayDataMutation,
  useDeleteManyPayDataMutation,
  useLazyXnttMailDraftQuery,
} from '../../store/api/procurementApi'
import type { PayDataListItem, PayDataDnttParts } from '../../types/api'
import { API_BASE_URL } from '@/config/api'
import { getStoredToken } from '@/services/auth'
import {
  base64ToBlob,
  errorDetail,
  filenameFromDisposition,
  isWindowsOS,
  openOutlookDraft,
  triggerDownload,
} from '@/utils/outlookMail'
import type { ProcurementFilters } from '@/pages/procurement/ProcurementPage'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  useRowMultiSelection,
} from '../../components/dataList'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { titleCaseName } from '../../utils/text'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import PayDataConfirmPrintDialog from './PayDataConfirmPrintDialog'
import PayDataFormDialog from './PayDataFormDialog'
import PayDataChangeBankDialog from './PayDataChangeBankDialog'
import PayDataAddInvoiceDialog from './PayDataAddInvoiceDialog'
import PayDataSendVatDialog from './PayDataSendVatDialog'

interface Props {
  filters: ProcurementFilters
}

type FilterKind = 'search'
type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof PayDataListItem & string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  /** Width mong muốn — dùng làm floor (min) cho width auto-đo; nếu > MAX_COL_WIDTH thì cũng nới trần riêng cho cột. */
  width?: number
  /** Bỏ trần MAX_COL_WIDTH — cột nới rộng theo content thật (vd tên Supplier dài). */
  uncapped?: boolean
  /** Title-case VALUE cell cho cột tên riêng (display-only) — [[feedback_proper_case_cell]]. */
  proper?: boolean
  /** Cột số nhưng KHÔNG cộng tổng ở footer (vd Requested FX — tỷ giá, tổng vô nghĩa). */
  noSum?: boolean
}

const ALL_ROWS_PAGE_SIZE = 1_000_000

const FROZEN_KEYS: ReadonlySet<string> = new Set(['contract_no'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { contract_no: 140 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { contract_no: 120 }

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const TICK_COL_WIDTH = 44

// Trần width auto-đo (§7.11) — cột chạm trần → wrap (§7.8) thay vì cắt "…".
const MAX_COL_WIDTH = 260


// Thứ tự + caption theo `ColumnOrder` của frmProcurementQryPayData (Access datasheet).
// checked_row (ColumnOrder 0) → render thành cột tick riêng (không trong TABLE_COLUMNS).
// Width default đo từ content thật (21k rows, p95/max độ dài đã format — Access
// ColumnWidth đa số -2 best-fit); user resize đè lên qua useColumnWidths.
const RAW_COLUMNS: ColumnDef[] = [
  { key: 'must_fix_txt', label: 'Alert Txt', wrap: true, width: 120 }, // 1 — đỏ khi có giá trị (mirror Invoice alert_txt)
  { key: 'paydata_p_id', label: 'ID', kind: 'int', align: 'right', width: 80 }, // 2
  { key: 'payment_date', label: 'Payment Date', kind: 'date', width: 110 }, // 3
  { key: 'contract_no', label: 'Contract No', width: 140 }, // 4 (frozen)
  { key: 'npe_ref_only', label: 'NPE Ref (if have)', width: 100 }, // 5
  { key: 'xntt_no', label: 'XNTT No', width: 140 }, // 6 — max "02/P-01151.2.NS.BH"
  { key: 'truck_id', label: 'Truck ID', width: 80 }, // 7
  { key: 'supplier', label: 'Supplier', wrap: true, proper: true, width: 380, uncapped: true }, // 9 — tên dài, bỏ trần cho hiện đủ
  { key: 'payment_type', label: 'Payment Type', filter: 'checkboxes', width: 110 }, // 10
  { key: 'bank', label: 'Request - Bank', filter: 'checkboxes', width: 110 }, // 11
  { key: 'quantity', label: 'Request - Quantity', kind: 'number', align: 'right', width: 100 }, // 12
  { key: 'price', label: 'Request - Price', kind: 'number', align: 'right', width: 100 }, // 13
  { key: 'price_unit', label: 'Request - PriceUnit', filter: 'checkboxes', width: 90 }, // 14
  { key: 'futures', label: 'Request - Futures', kind: 'number', align: 'right', width: 95 }, // 15
  { key: 'lot', label: 'Request - Lot', kind: 'number', align: 'right', width: 70 }, // 16
  { key: 'stoploss', label: 'Request - Stoploss', kind: 'number', align: 'right', width: 85 }, // 17
  { key: 'advanced', label: 'Request - Advanced', kind: 'number', align: 'right', width: 110 }, // 18
  { key: 'requested_usd', label: 'Requested USD', kind: 'number', align: 'right', width: 115 }, // 19 — Access 1728tw
  { key: 'requested_fx', label: 'Requested FX', kind: 'number', align: 'right', width: 85, noSum: true }, // 20 — tỷ giá, không cộng tổng
  { key: 'requested_vnd', label: 'Requested VND', kind: 'number', align: 'right', width: 125 }, // 21
  { key: 'vat_amount_vnd', label: 'VAT Amount VND', kind: 'number', align: 'right', width: 110 }, // 22
  { key: 'paid_usd', label: 'Actual Payment - USD', kind: 'number', align: 'right', width: 100 }, // 23
  { key: 'fx', label: 'Actual Payment - FX', kind: 'number', align: 'right', width: 80 }, // 24
  { key: 'paid_vnd', label: 'Actual Payment - VND', kind: 'number', align: 'right', width: 125 }, // 25
  { key: 'reconcile', label: 'Reconcile', kind: 'number', align: 'right', width: 130 }, // 26
  { key: 'interest_rate', label: 'Consignment - Interest Rate', kind: 'number', align: 'right', width: 75 }, // 27
  { key: 'fixation_deadline', label: 'Consignment - Fixation Deadline', kind: 'date', width: 105 }, // 28
  { key: 'invoice_no', label: 'Invoice No', width: 135 }, // 29
  { key: 'invoice_date', label: 'Invoice Date', kind: 'date', width: 110 }, // 30
  { key: 'invoice_amount', label: 'Invoice Amount', kind: 'number', align: 'right', width: 120 }, // 31
  { key: 'invoice_vat_amount', label: 'Invoice VAT', kind: 'number', align: 'right', width: 110 }, // 32
  { key: 'invoice_amount_after_tax', label: 'Invoice Amount After Tax', kind: 'number', align: 'right', width: 120 }, // 33
  { key: 'invoice_paid_vat_bankname', label: 'VAT Paid Request - Bank', width: 110 }, // 34
  { key: 'invoice_paid_vat_date', label: 'VAT Paid Request - Date', kind: 'date', width: 110 }, // 35
  // ----- Hidden by default (ColumnHidden) — bật được qua Columns menu -----
  { key: 'warehouse', label: 'Others - Warehouse', proper: true, width: 150 },
  { key: 'note', label: 'Others - Note', wrap: true, width: 200 },
  { key: 'supplier_id', label: 'BankInfo - Supplier ID', width: 85 },
  { key: 'bank_account', label: 'BankAcc Number', width: 140 },
  { key: 'account_name', label: 'BankAcc Name', proper: true, width: 350 },
  { key: 'bank_name', label: 'Bank Name', wrap: true, proper: true, width: 200 },
  { key: 'contract_date', label: 'Other - Contract Date', kind: 'date', width: 110 },
  { key: 'quality_code', label: 'Other - Quality', filter: 'checkboxes', width: 100 },
  { key: 'contract_ref_sub', label: 'Sub - Contract Ref', width: 160 },
  { key: 'shipper_name', label: 'Sub - Shipper Name', wrap: true, proper: true, width: 150 },
  { key: 'price_sub', label: 'Sub - Price', kind: 'number', align: 'right', width: 85 },
  { key: 'advanced_sub', label: 'Sub - Advanced', kind: 'number', align: 'right', width: 105 },
  { key: 'requested_usd_sub', label: 'Sub - Requested USD', kind: 'number', align: 'right', width: 90 },
  { key: 'requested_vnd_sub', label: 'Sub - Requested VND', kind: 'number', align: 'right', width: 105 },
  { key: 'invoice_no_sub', label: 'Sub - Invoice No', width: 130 },
  { key: 'bank_sub', label: 'Sub - Bank', width: 140 },
  { key: 'invoice_date_sub', label: 'Sub - Invoice Date', kind: 'date', width: 110 },
  { key: 'invoice_amount_sub', label: 'Sub - Invoice Amount', kind: 'number', align: 'right', width: 110 },
  { key: 'account_name_sub', label: 'Sub - BankAcc Name', proper: true, width: 140 },
  { key: 'bank_account_sub', label: 'Sub - BankAcc Number', width: 120 },
  { key: 'person_update', label: 'Updated Person', width: 210 },
  { key: 'date_update', label: 'Updated Date', kind: 'datetime', width: 140 },
  // Access: control 'Sub - Shipper Name' tạo sau cùng → nằm cuối datasheet.
]

// Default width = đo theo CONTENT thật của cột (RAW_COLUMNS.width) — KHÔNG phình
// theo độ dài tên cột. Header dài sẽ wrap 2 dòng (whiteSpace:normal ở header cell).
const TABLE_COLUMNS: ColumnDef[] = RAW_COLUMNS

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}


const sumNumericColumn = (rows: PayDataListItem[], key: keyof PayDataListItem): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const cellValue = (row: PayDataListItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  // ID: số nguyên thuần, KHÔNG dấu phân cách nghìn (Access format Integer/General).
  if (col.kind === 'int') return String(Math.round(Number(val)))
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (col.proper) return titleCaseName(val)
  return String(val)
}

export default function ProcurementPayDataListView({ filters }: Props) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const { canEdit } = usePagePermission()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()

  const [confirm, setConfirm] = useState<{ kind: 'delete-one' | 'delete-many'; message: string } | null>(null)
  const [printRow, setPrintRow] = useState<PayDataListItem | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy' } | null>(null)
  const [addInvoiceCtx, setAddInvoiceCtx] = useState<{ contractNo: string; ids: number[]; rows: PayDataListItem[] } | null>(null)
  const [changeBankCtx, setChangeBankCtx] = useState<{ cpCode: string; label: string; rows: PayDataListItem[] } | null>(null)
  const [sendVatCtx, setSendVatCtx] = useState<{ cpCode: string; label: string; ids: number[]; invoiceDate: string | null } | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof PayDataListItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.pay-data.list')

  const filterPopover = useFilterPopover<FilterKind>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof PayDataListItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'procurement',
    prefKey: 'columns',
    subKey: 'procurement-pay-data',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'procurement.pay-data.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error } = useListPayDataQuery(
    {
      delivery_latest_from: filters.startDate || undefined,
      delivery_latest_to: filters.endDate || undefined,
      is_forced_close:
        filters.isForcedClose === 'yes' ? true : filters.isForcedClose === 'no' ? false : undefined,
      page: 1,
      page_size: ALL_ROWS_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: true, skip: filters.startDate > filters.endDate },
  )

  const [deletePayData, { isLoading: deleteLoading }] = useDeletePayDataMutation()
  const [deleteManyPayData, { isLoading: deleteManyLoading }] = useDeleteManyPayDataMutation()
  const [sendingDntt, setSendingDntt] = useState(false)
  // Send-XNTT nhánh Mac (mailto) dùng lazy query /mail/xntt; Windows dùng .eml (fetch).
  const [triggerXnttDraft] = useLazyXnttMailDraftQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && !col.noSum)
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  // Default width MỖI cột tự đo theo text thật (header + sample data) — §7.11.
  // Cột ngắn (id/date/số) gọn 1 dòng; cột dài chạm MAX rồi wrap (§7.8). User
  // resize (getWidth) hoặc freeze fallback vẫn override.
  const autoColWidths = useMemo<Record<string, number>>(() => {
    const CHAR_PX = 7.2
    const HEADER_PAD = 44 // padding + sort suffix + filter ▽ + resize handle
    const CELL_PAD = 24
    const MIN = 72
    const sample = filteredRows.slice(0, 400)
    const out: Record<string, number> = {}
    for (const col of visibleColumns) {
      const key = String(col.key)
      let bodyMax = 0
      for (const r of sample) {
        const len = cellValue(r, col).length
        if (len > bodyMax) bodyMax = len
      }
      const headerPx = col.label.length * CHAR_PX + HEADER_PAD
      const bodyPx = bodyMax * CHAR_PX + CELL_PAD
      const minForType = col.kind === 'date' || col.kind === 'datetime' ? 118 : MIN
      // col.width = floor (min); cột uncapped bỏ trần, còn lại trần = max(MAX_COL_WIDTH, col.width).
      const floor = col.width ?? 0
      const ceiling = col.uncapped ? Number.POSITIVE_INFINITY : Math.max(MAX_COL_WIDTH, floor)
      out[key] = Math.round(Math.min(ceiling, Math.max(minForType, headerPx, bodyPx, floor)))
    }
    return out
  }, [visibleColumns, filteredRows])

  const resolveWidth = (key: string, isFrozen: boolean): number =>
    getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : autoColWidths[key] ?? 100)

  const minTableWidth = useMemo(
    () =>
      visibleColumns.reduce((acc, col) => acc + resolveWidth(String(col.key), FROZEN_KEYS.has(String(col.key))), TICK_COL_WIDTH),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH, autoColWidths],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof PayDataListItem) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<PayDataListItem | null>(
    () => (selectedId == null ? null : rows.find((r) => r.paydata_p_id === selectedId) ?? null),
    [rows, selectedId],
  )

  // Access frmProcurementQryPayData.Form_Load → `DoCmd.GoToRecord , , acLast` = record CUỐI.
  // → auto-select dòng cuối + scroll xuống 1 lần sau khi list load; KHÔNG đè lựa chọn thủ công.
  const virtuosoRef = useRef<TableVirtuosoHandle>(null)
  const didAutoSelectRef = useRef(false)
  useEffect(() => {
    if (isFetching || didAutoSelectRef.current) return
    if (rows.length > 0) {
      setSelectedId(rows[rows.length - 1].paydata_p_id)
      didAutoSelectRef.current = true
    }
  }, [isFetching, rows, setSelectedId])

  // --- Tick helpers (Access checked_row) — hook chung useRowMultiSelection.
  // Add Invoice / Change Bank / Delete-many / Send VAT resolve theo filteredRows.
  const {
    selectedIds: tickedIds,
    setSelectedIds: setTickedIds,
    selectedRows: tickedRows,
    toggle: toggleTick,
    toggleAll: handleTickAll,
    allVisibleSelected: allFilteredTicked,
  } = useRowMultiSelection(filteredRows, (r) => r.paydata_p_id)

  // [feedback] Focus 1 dòng ⇔ tick dòng đó: các nút thao-tác-theo-tick (Add Invoice /
  // Change Bank / Send VAT / Send-DNTT / Send-XNTT / Delete) coi dòng đang focus như
  // 1 tick khi CHƯA tick checkbox nào. Ưu tiên tập đã tick; nếu rỗng thì fallback
  // selectedRow. Không union để tránh gộp nhầm dòng focus khác contract/supplier.
  const effectiveTickedRows = useMemo<PayDataListItem[]>(
    () => (tickedRows.length > 0 ? tickedRows : selectedRow ? [selectedRow] : []),
    [tickedRows, selectedRow],
  )
  // Guard 1-giá-trị proactive: tick >1 supplier / >1 contract thì disable nút
  // liên quan luôn (không đợi click mới snack).
  //  • multiSupplier  → Change Bank (Access `btnChangeBank`, cp_code) + Send
  //    DNTT/XNTT/VAT (ràng buộc thêm ngoài Access theo yêu cầu — 1 mail/1 NCC).
  //  • multiContract  → Add Invoice (Access `btnInvoice`, contract_no).
  const multiSupplier = useMemo(
    () => new Set(effectiveTickedRows.map((r) => String(r.cp_code ?? ''))).size > 1,
    [effectiveTickedRows],
  )
  const multiContract = useMemo(
    () => new Set(effectiveTickedRows.map((r) => String(r.contract_no ?? ''))).size > 1,
    [effectiveTickedRows],
  )
  // Edit/Copy chỉ tác động 1 dòng → tick >= 2 dòng thì disable.
  const multiTicked = tickedRows.length > 1
  // Access: nút Edit/Copy/Print/single-Delete mở đầu bằng check Form![subForm]!paydata_p_id.
  const requireRow = (): PayDataListItem | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }
  const requireTicked = (): PayDataListItem[] | null => {
    if (effectiveTickedRows.length === 0) {
      snack.warning('Please tick all Paydata ID which you want to edit!')
      return null
    }
    return effectiveTickedRows
  }

  // --- Toolbar handlers ---
  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new' })
  }
  const handleEdit = () => {
    if (!canEdit) return
    if (!requireRow()) return
    setFormState({ mode: 'edit' })
  }
  const handleCopy = () => {
    if (!canEdit) return
    if (!requireRow()) return
    setFormState({ mode: 'copy' })
  }
  const handleAddInvoice = () => {
    if (!canEdit) return
    const ticked = requireTicked()
    if (!ticked) return
    const contracts = new Set(ticked.map((r) => String(r.contract_no ?? '')))
    if (contracts.size > 1) {
      snack.info('Please choose only one contract!')
      return
    }
    setAddInvoiceCtx({ contractNo: String(ticked[0].contract_no ?? ''), ids: ticked.map((r) => r.paydata_p_id), rows: ticked })
  }
  const handleChangeBank = () => {
    if (!canEdit) return
    const ticked = requireTicked()
    if (!ticked) return
    const suppliers = new Set(ticked.map((r) => String(r.cp_code ?? '')))
    if (suppliers.size > 1) {
      snack.info('Please choose only one Supplier!')
      return
    }
    setChangeBankCtx({
      cpCode: String(ticked[0].cp_code ?? ''),
      label: String(ticked[0].supplier ?? ticked[0].cp_code ?? ''),
      rows: ticked,
    })
  }
  const handleDelete = () => {
    if (!canEdit) return
    if (tickedRows.length > 0) {
      if (tickedRows.length > 10) {
        snack.warning('Cannot delete more than 10 records at once. Please tick 10 or fewer.')
        return
      }
      setConfirm({ kind: 'delete-many', message: `Do you want to delete ${tickedRows.length} records ?` })
      return
    }
    const row = requireRow()
    if (!row) return
    setConfirm({
      kind: 'delete-one',
      message: `Do you want to delete this paydata with ID = '${row.paydata_p_id}'?`,
    })
  }
  // Pay-Confirm print — BE chọn variant theo row (rpProcurementPayConfirm_*).
  const handlePrintXntt = () => {
    // View cũng xem được Print Preview (chỉ ẩn nút Print thật trong dialog).
    const row = requireRow()
    if (!row) return
    setPrintRow(row)
  }
  // Send-DNTT (Access btnDNTT) → mở Outlook desktop soạn thư (có đính kèm Excel).
  //  • Windows: tải .eml (X-Unsent:1) → double-click bung Outlook, TỰ đính kèm.
  //  • Mac/khác: Outlook-for-Mac bỏ qua X-Unsent (.eml read-only) → mở mailto
  //    (bung Outlook desktop soạn thư) + tải Excel để user KÉO đính kèm tay.
  const handleSendDntt = async () => {
    if (!canEdit) return
    const ticked = requireTicked()
    if (!ticked) return
    const ids = ticked.map((r) => r.paydata_p_id)
    const auth = { Authorization: `Bearer ${getStoredToken() ?? ''}` }
    setSendingDntt(true)
    try {
      if (isWindowsOS()) {
        // Windows: .eml auto-đính kèm.
        const resp = await fetch(
          `${API_BASE_URL}/purchase/pay-data/mail/dntt?paydataIds=${ids.join(',')}`,
          { headers: auth },
        )
        if (!resp.ok) {
          snack.error(await errorDetail(resp, 'Could not create DNTT email.'))
          return
        }
        const blob = await resp.blob()
        const filename = filenameFromDisposition(resp.headers.get('Content-Disposition') ?? '', 'DNTT.eml')
        triggerDownload(blob, filename)
        snack.success('Downloaded .eml — double-click to open Outlook, review then click Send.')
      } else {
        // Mac/khác: draft (mailto) + Excel base64 (đính kèm tay).
        const resp = await fetch(
          `${API_BASE_URL}/purchase/pay-data/mail/dntt/parts?paydataIds=${ids.join(',')}`,
          { headers: auth },
        )
        if (!resp.ok) {
          snack.error(await errorDetail(resp, 'Could not create DNTT email.'))
          return
        }
        const parts: PayDataDnttParts = await resp.json()
        // Tải Excel trước để user có sẵn file kéo vào thư.
        if (parts.excel_base64) {
          triggerDownload(
            base64ToBlob(parts.excel_base64, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
            parts.excel_filename || 'DNTT.xlsx',
          )
        }
        openOutlookDraft({ to: parts.to, cc: parts.cc, subject: parts.subject, body: parts.body })
        snack.info('Outlook mở sẵn thư — hãy KÉO file Excel vừa tải vào email rồi bấm Send.')
      }
    } catch (e) {
      snack.error(getErrorMessage(e))
    } finally {
      setSendingDntt(false)
    }
  }
  // Send-XNTT (Access btnXNTT): KHÔNG đính kèm → mở Outlook qua mailto, điền sẵn
  // To (TẠM ép email test)/Subject/Body để user review & tự bấm Send.
  // Nếu đã prefetch xong cho đúng lựa chọn → mở mailto NGAY trong cú click (không
  // await) để Chrome nhớ "Always allow". Chưa kịp → fallback fetch (có thể hỏi lại
  // 1 lần cho tới khi prefetch sẵn).
  // Send-XNTT (Access btnXNTT) — KHÔNG đính kèm.
  //  • Windows: tải .eml (X-Unsent, body HTML giữ <b> in đậm như Access .HTMLBody)
  //    → double-click mở Outlook classic soạn thư. KHÔNG mở mailto (tránh log
  //    "Launched external handler" + giữ được định dạng).
  //  • Mac/khác: mailto (mở Outlook desktop), body plain (mailto không mang HTML).
  const handleSendXntt = async () => {
    if (!canEdit) return
    const ticked = requireTicked()
    if (!ticked) return
    const ids = ticked.map((r) => r.paydata_p_id)
    if (isWindowsOS()) {
      try {
        const resp = await fetch(
          `${API_BASE_URL}/purchase/pay-data/mail/xntt/eml?paydataIds=${ids.join(',')}`,
          { headers: { Authorization: `Bearer ${getStoredToken() ?? ''}` } },
        )
        if (!resp.ok) {
          snack.error(await errorDetail(resp, 'Could not create XNTT email.'))
          return
        }
        const blob = await resp.blob()
        const filename = filenameFromDisposition(resp.headers.get('Content-Disposition') ?? '', 'XNTT.eml')
        triggerDownload(blob, filename)
        snack.success('Downloaded .eml — double-click to open Outlook, review then click Send.')
      } catch (e) {
        snack.error(getErrorMessage(e))
      }
      return
    }
    void triggerXnttDraft(ids)
      .unwrap()
      .then((draft) => openOutlookDraft(draft))
      .catch((e) => snack.error(getErrorMessage(e)))
  }
  // Send-DNTT VAT (Access btnVAT_Click): seed dialog từ DÒNG TICKED ĐẦU TIÊN
  // (Access DLookup checked_row=-1) — cp_code + invoice_date. KHÔNG chặn nhiều
  // supplier (Access không có): trong dialog cp_code là combobox chọn lại được.
  const handleSendVat = () => {
    if (!canEdit) return
    const ticked = requireTicked()
    if (!ticked) return
    const first = ticked[0]
    setSendVatCtx({
      cpCode: String(first.cp_code ?? ''),
      label: String(first.supplier ?? first.cp_code ?? ''),
      ids: ticked.map((r) => r.paydata_p_id),
      invoiceDate: first.invoice_date ? String(first.invoice_date) : null,
    })
  }

  const runConfirm = async () => {
    if (!confirm) return
    try {
      if (confirm.kind === 'delete-many') {
        await deleteManyPayData(tickedRows.map((r) => r.paydata_p_id)).unwrap()
        setTickedIds(new Set())
      } else if (selectedRow) {
        await deletePayData(selectedRow.paydata_p_id).unwrap()
        setSelectedId(null)
      }
      snack.success('Successfully Deleted')
    } catch (e) {
      snack.error(getErrorMessage(e))
    } finally {
      setConfirm(null)
    }
  }

  const deleteBusy = deleteLoading || deleteManyLoading

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="outlined" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit || multiTicked} onClick={handleEdit} startIcon={<EditIcon sx={{ fontSize: 16 }} />} title={multiTicked ? 'Please choose only one record!' : 'Edit'}>
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit || multiTicked} onClick={handleCopy} startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />} title={multiTicked ? 'Please choose only one record!' : 'Copy'}>
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={effectiveTickedRows.length === 0 || !canEdit || multiContract} onClick={handleAddInvoice} startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />} title={multiContract ? 'Please choose only one contract!' : 'Add Invoice'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Add Invoice</Box>
            </Button>
            <Button variant="outlined" size="small" color="secondary" sx={actionToolbarBtnSx} disabled={effectiveTickedRows.length === 0 || !canEdit || multiSupplier} onClick={handleChangeBank} startIcon={<AccountBalanceIcon sx={{ fontSize: 16 }} />} title={multiSupplier ? 'Please choose only one Supplier!' : 'Change Bank'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Change Bank</Box>
            </Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={effectiveTickedRows.length === 0 || !canEdit || deleteBusy} onClick={handleDelete} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow || multiSupplier} onClick={handlePrintXntt} startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />} title={multiSupplier ? 'Please choose only one Supplier!' : 'Pr-XNTT'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Pr-</Box>XNTT
            </Button>
            <Button variant="outlined" size="small" color="info" sx={actionToolbarBtnSx} disabled={effectiveTickedRows.length === 0 || !canEdit || sendingDntt || multiSupplier} onClick={handleSendDntt} startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />} title={multiSupplier ? 'Please choose only one Supplier!' : 'Send-DNTT'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Send-</Box>DNTT
            </Button>
            <Button variant="outlined" size="small" color="info" sx={actionToolbarBtnSx} disabled={effectiveTickedRows.length === 0 || !canEdit || multiSupplier} onClick={handleSendVat} startIcon={<RequestQuoteOutlinedIcon sx={{ fontSize: 16 }} />} title={multiSupplier ? 'Please choose only one Supplier!' : 'Send-DNTT VAT'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Send-DNTT VAT</Box>
            </Button>
            <Button variant="outlined" size="small" color="info" sx={actionToolbarBtnSx} disabled={effectiveTickedRows.length === 0 || !canEdit || multiSupplier} onClick={handleSendXntt} startIcon={<SendOutlinedIcon sx={{ fontSize: 16 }} />} title={multiSupplier ? 'Please choose only one Supplier!' : 'Send-XNTT'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Send-</Box>XNTT
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e: MouseEvent<HTMLElement>) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Could not load Pay Data list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            {tickedRows.length > 0 && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
                Ticked: {tickedRows.length}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract no, XNTT, supplier, invoice…"
              label="Search Pay Data"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState tone="error" title="Could not load Pay Data list" description="Please refresh the page or contact the system administrator if the issue continues." />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Pay Data" description="Try adjusting the Delivery Latest date range or the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              ref={virtuosoRef}
              style={{ height: '100%' }}
              data={rows}
              initialTopMostItemIndex={rows.length > 0 ? { index: rows.length - 1, align: 'end' } : 0}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell app-sticky-col"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0, zIndex: 101 }}
                    sx={{ textAlign: 'center' }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ p: 0 }}
                      checked={allFilteredTicked}
                      indeterminate={!allFilteredTicked && tickedRows.length > 0}
                      onChange={handleTickAll}
                      disabled={!canEdit}
                      title="Tick all (filtered)"
                    />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = resolveWidth(key, isFrozen)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.left = TICK_COL_WIDTH
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-table-header-cell app-sticky-col-2' : 'app-table-header-cell'}
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  <TableCell padding="checkbox" className="app-sticky-col" style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }} sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = resolveWidth(key, isFrozen)
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...(isFrozen ? { left: TICK_COL_WIDTH } : {}) }}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          ...(isFrozen ? {} : { bgcolor: 'background.paper' }),
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: PayDataListItem) => (
                <>
                  <TableCell
                    padding="checkbox"
                    className="app-sticky-col"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }}
                    sx={{ textAlign: 'center', py: 0.25 }}
                    onClick={(e) => { e.stopPropagation(); if (canEdit) toggleTick(row.paydata_p_id) }}
                  >
                    <Checkbox size="small" sx={{ p: 0 }} checked={tickedIds.has(row.paydata_p_id)} onChange={() => toggleTick(row.paydata_p_id)} onClick={(e) => e.stopPropagation()} disabled={!canEdit} />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const val = cellValue(row, col)
                    const w = resolveWidth(key, isFrozen)
                    const wrap = col.wrap || getWidth(key) != null || (!isFrozen && autoColWidths[key] >= MAX_COL_WIDTH)
                    const frozenStyle: CSSProperties = isFrozen ? { left: TICK_COL_WIDTH } : {}
                    // Access ConditionalFormat: must_fix_txt tô đỏ khi có giá trị
                    // (mirror alert_txt của Procurement Invoice).
                    const isAlert = key === 'must_fix_txt' && !!val
                    if (wrap) {
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col-2' : undefined}
                          style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...frozenStyle }}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top', ...(isAlert ? { color: 'error.main', fontWeight: 600 } : {}) }}
                        >
                          <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...frozenStyle }}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top', ...(isAlert ? { color: 'error.main', fontWeight: 600 } : {}) }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
              components={{
                Table: (props) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && row.paydata_p_id === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row) toggleSelect(row.paydata_p_id)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <ConfirmDialog
        open={!!confirm}
        title="Delete Paydata"
        message={confirm?.message ?? ''}
        onConfirm={() => void runConfirm()}
        onClose={() => setConfirm(null)}
      />

      <PayDataConfirmPrintDialog
        open={printRow != null}
        canEdit={canEdit}
        paydataId={printRow?.paydata_p_id ?? null}
        xnttNo={printRow?.xntt_no ?? null}
        paymentType={printRow?.payment_type ?? null}
        contractNo={printRow?.contract_no ?? null}
        onClose={() => setPrintRow(null)}
      />

      {formState && (
        <PayDataFormDialog
          open
          mode={formState.mode}
          paydataId={formState.mode === 'new' ? null : selectedRow?.paydata_p_id ?? null}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}

      <PayDataAddInvoiceDialog
        open={addInvoiceCtx != null}
        contractNo={addInvoiceCtx?.contractNo ?? ''}
        paydataIds={addInvoiceCtx?.ids ?? []}
        rows={addInvoiceCtx?.rows ?? []}
        canEdit={canEdit}
        onClose={() => setAddInvoiceCtx(null)}
        onSaved={() => setTickedIds(new Set())}
      />

      <PayDataChangeBankDialog
        open={changeBankCtx != null}
        cpCode={changeBankCtx?.cpCode ?? ''}
        supplierLabel={changeBankCtx?.label ?? ''}
        rows={changeBankCtx?.rows ?? []}
        canEdit={canEdit}
        onClose={() => setChangeBankCtx(null)}
        onSaved={() => setTickedIds(new Set())}
      />

      <PayDataSendVatDialog
        open={sendVatCtx != null}
        cpCode={sendVatCtx?.cpCode ?? ''}
        supplierLabel={sendVatCtx?.label ?? ''}
        paydataIds={sendVatCtx?.ids ?? []}
        invoiceDate={sendVatCtx?.invoiceDate ?? null}
        canEdit={canEdit}
        onClose={() => setSendVatCtx(null)}
        onSaved={() => setTickedIds(new Set())}
      />
    </Box>
  )
}
